import React, { useEffect, useState, useRef } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FaBan,
  FaCalendarCheck,
  FaCoins,
  FaDollarSign,
  FaEye,
  FaHistory,
  FaPenAlt,
  FaRemoveFormat,
  FaTag,
  FaTrash,
  FaUndo,
} from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import moment from "moment";
import InvoicePreview from "../GeneratePDF/InvoicePreview";
import InvoiceXeroPreview from "../Finance/InvoiceXeroPreview";
import ApplicationGstSetupModal from "../Applications/ApplicationGstSetupModal";
import InvoiceCreateWizard from "../Finance/InvoiceCreateWizard";
import {
  buildLocalInvoiceNo,
  getClientEmailFromRecord,
  getClientIdFromRecord,
  getClientNameFromRecord,
  getClientInitials,
  isApplicationGstDetailsMissing,
  resolveGstModeType,
  inferGstModeFromAmounts,
  formatGstModeLabel,
} from "../../utilis/applicationCostHelpers";
import {
  buildReceiptPreviewUrl,
  isAllowedReceiptFile,
  isReceiptImageFile,
} from "../../utilis/financeInvoicePaymentHelpers";
import { calculateGstBreakdown } from "../../utilis/gstCalculation";
import {
  incrementInvoiceNumber,
  mergeInvoiceRecordsForNumbering,
} from "../../utilis/invoiceNumberHelpers";
import {
  deriveInvoiceStatusFromPayment,
  getInvoiceStatusBadgeClass,
  getInvoiceStatusLabel,
  normalizeInvoiceStatus,
} from "../../utilis/invoiceStatusHelpers";
import {
  getFinanceInvoiceApiMessage,
  isFinanceInvoiceApiSuccess,
  normalizeFinanceInvoiceList,
} from "../../utilis/invoiceCreateHelpers";
import {
  buildInvoiceXeroPreviewDraftFromRecord,
  getLedgerInvoiceId,
  getLedgerInvoiceNumber,
  isInvoiceNotesApiSuccess,
  normalizeFinanceInvoiceFromRecord,
  normalizeFinanceInvoiceRecord,
  normalizeInvoiceNotesList,
} from "../../utilis/invoiceEditHelpers";
import "./clients.css";

const TableSkeleton = ({ rows = 3 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i}>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "40px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "56px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "80px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "60px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "100px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "150px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "120px", height: "16px" }}
          ></div>
        </td>
        <td style={{ padding: "8px" }}>
          <div
            className="bg-light rounded"
            style={{ width: "120px", height: "16px" }}
          ></div>
        </td>
      </tr>
    ))}
  </tbody>
);

const PaymentPlanSkeleton = () => (
  <div className="animate-pulse">
    <div style={{ overflowX: "auto" }}>
      <table
        className="table table-sm align-middle"
        style={{ marginBottom: 0 }}
      >
        <thead>
          <tr>
            <th>#</th>
            <th>Due Date</th>
            <th>Amount</th>
            <th>Paid Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              <td>
                <div
                  className="bg-light rounded"
                  style={{ width: 30, height: 16 }}
                ></div>
              </td>
              <td>
                <div
                  className="bg-light rounded"
                  style={{ width: 80, height: 16 }}
                ></div>
              </td>
              <td>
                <div
                  className="bg-light rounded"
                  style={{ width: 70, height: 16 }}
                ></div>
              </td>
              <td>
                <div
                  className="bg-light rounded"
                  style={{ width: 80, height: 16 }}
                ></div>
              </td>
              <td>
                <div
                  className="bg-light rounded"
                  style={{ width: 60, height: 20, fontSize: "12px" }}
                ></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const FinancialSummarySkeleton = () => {
  const skeletonStyle = {
    backgroundColor: "#e5e7eb",
    borderRadius: 4,
    animation: "pulse 1.5s infinite ease-in-out",
  };
  return (
    <div className="financial-summary-xero">
      <div className="financial-summary-xero__grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="financial-summary-xero__metric">
            <div style={{ ...skeletonStyle, width: 56, height: 10, marginBottom: 6 }} />
            <div style={{ ...skeletonStyle, width: 72, height: 14 }} />
          </div>
        ))}
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
};

const SummaryMetric = ({
  label,
  value,
  valueClassName = "",
  emphasis = false,
}) => (
  <div
    className={`financial-summary-xero__metric${
      emphasis ? " financial-summary-xero__metric--emphasis" : ""
    }`}
  >
    <span className="financial-summary-xero__label">{label}</span>
    <span className={`financial-summary-xero__value ${valueClassName}`.trim()}>
      {value}
    </span>
  </div>
);

const FinancialSummary = ({
  currency,
  fmt,
  applicationGstInfo,
  departmentGstInfo,
  ledgerTotals,
  isMigration = false,
  departmentFeeCurrency = "",
}) => {
  const showCostBreakdown =
    applicationGstInfo.baseCost > 0 ||
    (applicationGstInfo.type !== "notax" && applicationGstInfo.amount > 0);
  const showDepartmentBase =
    isMigration && Number(departmentGstInfo?.baseCost) > 0;
  const showDepartmentGst =
    isMigration &&
    departmentGstInfo?.type !== "notax" &&
    Number(departmentGstInfo?.amount) > 0;
  const departmentCurrency = departmentFeeCurrency || currency;
  const remainingBalance = Number(ledgerTotals?.remaining_balance || 0);
  const isDue = remainingBalance > 0;

  return (
    <div className="financial-summary-xero">
      <div className="financial-summary-xero__grid">
        {showCostBreakdown && applicationGstInfo.baseCost > 0 && (
          <SummaryMetric
            label="Professional cost"
            value={`${currency} ${fmt(applicationGstInfo.baseCost)}`}
          />
        )}

        {showCostBreakdown &&
          applicationGstInfo.type !== "notax" &&
          applicationGstInfo.amount > 0 && (
            <div className="financial-summary-xero__metric">
              <span className="financial-summary-xero__label">
                Professional GST ({applicationGstInfo.percentage}%)
              </span>
              <span className="financial-summary-xero__value financial-summary-xero__value--gst">
                <span
                  className={`financial-summary-xero__gst-badge ${
                    applicationGstInfo.type === "exclusive"
                      ? "financial-summary-xero__gst-badge--exclusive"
                      : "financial-summary-xero__gst-badge--inclusive"
                  }`}
                >
                  {applicationGstInfo.type}
                </span>
                {currency} {fmt(applicationGstInfo.amount)}
                {applicationGstInfo.type === "inclusive" && (
                  <span className="financial-summary-xero__gst-included">incl.</span>
                )}
              </span>
            </div>
          )}

        {showDepartmentBase && (
          <SummaryMetric
            label="Department base fee"
            value={`${departmentCurrency} ${fmt(departmentGstInfo.baseCost)}`}
          />
        )}

        {showDepartmentGst && (
          <div className="financial-summary-xero__metric">
            <span className="financial-summary-xero__label">
              Department GST ({departmentGstInfo.percentage}%)
            </span>
            <span className="financial-summary-xero__value financial-summary-xero__value--gst">
              <span
                className={`financial-summary-xero__gst-badge ${
                  departmentGstInfo.type === "exclusive"
                    ? "financial-summary-xero__gst-badge--exclusive"
                    : "financial-summary-xero__gst-badge--inclusive"
                }`}
              >
                {departmentGstInfo.type}
              </span>
              {departmentCurrency} {fmt(departmentGstInfo.amount)}
              {departmentGstInfo.type === "inclusive" && (
                <span className="financial-summary-xero__gst-included">incl.</span>
              )}
            </span>
          </div>
        )}

        <SummaryMetric
          label="Charges"
          value={`${currency} ${fmt(ledgerTotals?.total_charges)}`}
        />
        <SummaryMetric
          label="Discounts"
          value={`- ${currency} ${fmt(ledgerTotals?.total_discounts)}`}
          valueClassName="financial-summary-xero__value--discount"
        />
        <SummaryMetric
          label="Payments"
          value={`- ${currency} ${fmt(ledgerTotals?.total_payments)}`}
          valueClassName="financial-summary-xero__value--payment"
        />
        <SummaryMetric
          label="Balance due"
          value={`${currency} ${fmt(remainingBalance)}`}
          valueClassName={
            isDue
              ? "financial-summary-xero__value--due-positive"
              : "financial-summary-xero__value--due-zero"
          }
          emphasis
        />
        <SummaryMetric
          label="Credit"
          value={`${currency} ${fmt(ledgerTotals?.total_credits)}`}
          valueClassName="financial-summary-xero__value--credit"
        />
        <SummaryMetric
          label="Refunded"
          value={`- ${currency} ${fmt(ledgerTotals?.total_refund_payment)}`}
          valueClassName="financial-summary-xero__value--discount"
        />
      </div>
    </div>
  );
};

const DiscountModal = ({ onClose, selectedApplication, type, clientData }) => {
  const navigate = useNavigate();
  const [discountFeeRules, setDiscountFeeRules] = useState([]);

  const [discountDescription, setDiscountDescription] = useState("");

  // Payments
  const [payment, setPayment] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    method: "cash",
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [refundReceiptFile, setRefundReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState("");
  const [refundReceiptPreview, setRefundReceiptPreview] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [planFrequency, setPlanFrequency] = useState("monthly");
  const [planInstallmentsCount, setPlanInstallmentsCount] = useState(3);
  const [planStartDate, setPlanStartDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [reasonForChangePlan, setReasonForChangePlan] = useState("");
  const [customPlanRows, setCustomPlanRows] = useState([]);
  const [loadingInstallment, setLoadingInstallment] = useState(false);
  const [loadingDeletePlan, setLoadingDeletePlan] = useState(false);

  // Loading states
  const [loading, setLoading] = useState({
    initial: true,
    ledger: false,
    discount: false,
    payment: false,
    plan: false,
    credit: false,
  });
  const [applicationLedgerDetails, setApplicationLedgerDetails] = useState([]);
  const [applicationCostDetail, setApplicationCostDetail] = useState(null);
  const [loadingInitials, setLoadingInitials] = useState(false);
  const [loadingCostDetail, setLoadingCostDetail] = useState(false);
  const [loadingPaymentInstallments, setLoadingPaymentInstallments] =
    useState(false);
  const [ledgerTotals, setLedgerTotals] = useState({
    total_charges: 0,
    total_discounts: 0,
    total_payments: 0,
    remaining_balance: 0,
    total_credits: 0,
  });
  const [activePlan, setActivePlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [applicationInvoices, setApplicationInvoices] = useState([]);
  const [clientInvoicesForNumbering, setClientInvoicesForNumbering] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [showInvoiceCreateWizard, setShowInvoiceCreateWizard] = useState(false);
  const [showInvoiceEditWizard, setShowInvoiceEditWizard] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [updatingInvoice, setUpdatingInvoice] = useState(false);
  const [updatingInvoiceDiscount, setUpdatingInvoiceDiscount] = useState(false);
  const [loadingEditInvoice, setLoadingEditInvoice] = useState(false);
  const [addingInvoiceNote, setAddingInvoiceNote] = useState(false);
  const [editingInvoiceNotes, setEditingInvoiceNotes] = useState([]);
  const [loadingInvoiceNotes, setLoadingInvoiceNotes] = useState(false);
  const [showInvoicePreviewModal, setShowInvoicePreviewModal] = useState(false);
  const [selectedHistoryInvoice, setSelectedHistoryInvoice] = useState(null);

  // Invoice state
  const [invoiceNo, setInvoiceNo] = useState();
  const [invoiceSettings, setInvoiceSettings] = useState({});
  const [invoiceReference, setInvoiceReference] = useState("");
  const [invoiceDefaultDescription, setInvoiceDefaultDescription] =
    useState("");
  const [showDiscount, setShowDiscount] = useState(false);
  const [invoiceCostDate, setInvoiceCostDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [invoiceDueDate, setInvoiceDueDate] = useState(
    selectedApplication?.due_date,
  );
  const [bankDetails, setBankDetails] = useState(null);
  const [planDetail, setPlanDetail] = useState([]);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundDate, setRefundDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [refundReason, setRefundReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [creditsAvailable, setCreditsAvailable] = useState([]);
  const [addItem, setAddItem] = useState(false);
  const [showGstSetupModal, setShowGstSetupModal] = useState(false);

  useEffect(() => {
    fetchApplicationLedgerDetail();
    getApplicationPaymentPlan();
    fetchApplicationCostDetail();
  }, [selectedApplication?.id, type]);

  useEffect(() => {
    const appId = selectedApplication?.id;
    const clientId =
      getClientIdFromRecord(clientData) || selectedApplication?.clientid;
    setInvoiceNo(buildLocalInvoiceNo(appId, clientId));
  }, [selectedApplication?.id, selectedApplication?.clientid, clientData]);

  useEffect(() => {
    if (clientData) {
      fetchAvailableCredits();
    }
  }, [clientData]);

  useEffect(() => {
    if (clientData && selectedApplication) {
      setInvoiceDefaultDescription(
        `Professional Cost: ${selectedApplication?.coursename}
Client Name: ${getClientNameFromRecord(clientData)}`,
      );
    }
  }, [selectedApplication, clientData]);

  useEffect(() => {
    if (applicationLedgerDetails) {
      applicationLedgerDetails.map((entry) => {
        if (entry.type === "charge") {
          setShowDiscount(true);
          setDiscountDescription("Discount/Waive reason:");
        }
      });
    }
  }, [applicationLedgerDetails]);

  useEffect(() => {
    const getInvoiceSetting = async () => {
      const response = await apiUrl.get("/getinvoiceSetting");
      if (response.status) {
        setBankDetails(response.data.data);
      }
    };
    getInvoiceSetting();
  }, []);

  const getAllInvoiceForApplication = async () => {
    const applicationId = selectedApplication?.id;
    const clientId =
      getClientIdFromRecord(clientData) || selectedApplication?.clientid;

    if (!applicationId || !clientId) {
      setApplicationInvoices([]);
      setLoadingInvoices(false);
      return;
    }

    try {
      setLoadingInvoices(true);
      const response = await apiUrl.get("finance-invoices", {
        params: {
          client_id: clientId,
          application_id: applicationId,
        },
      });

      if (isFinanceInvoiceApiSuccess(response)) {
        setApplicationInvoices(normalizeFinanceInvoiceList(response));
      } else {
        setApplicationInvoices([]);
      }
    } catch (error) {
      console.error(error);
      setApplicationInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchClientInvoicesForNumbering = async () => {
    const clientId =
      getClientIdFromRecord(clientData) || selectedApplication?.clientid;

    if (!clientId) {
      setClientInvoicesForNumbering([]);
      return;
    }

    try {
      const response = await apiUrl.get("finance-invoices", {
        params: {
          client_id: clientId,
        },
      });

      if (isFinanceInvoiceApiSuccess(response)) {
        setClientInvoicesForNumbering(normalizeFinanceInvoiceList(response));
      } else {
        setClientInvoicesForNumbering([]);
      }
    } catch (error) {
      console.error("Failed to load client invoices for numbering", error);
      setClientInvoicesForNumbering([]);
    }
  };

  const refreshInvoiceNumberingSources = async () => {
    await Promise.all([
      getAllInvoiceForApplication(),
      fetchClientInvoicesForNumbering(),
    ]);
  };

  const invoiceNumberingInvoices = React.useMemo(
    () =>
      mergeInvoiceRecordsForNumbering(
        applicationInvoices,
        clientInvoicesForNumbering,
      ),
    [applicationInvoices, clientInvoicesForNumbering],
  );

  useEffect(() => {
    getAllInvoiceForApplication();
    fetchClientInvoicesForNumbering();
  }, [selectedApplication?.id, clientData?.id, selectedApplication?.clientid]);

  const fetchAvailableCredits = async () => {
    const client_id = clientData?.id;
    try {
      if (client_id) {
        const response = await apiUrl.get(`/creditsAvailable/${client_id}`, {
          params: { currency: selectedApplication?.currency },
        });
        if (response.status) {
          setCreditsAvailable(response?.data?.data);
        }
      }
    } catch (error) {
      console.log("Error while fetching client credits available", error);
    }
  };

  const handleRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      return Swal.fire("Error", "Enter a valid refund amount.", "error");
    }
    if (amount > ledgerTotals.total_payments) {
      return Swal.fire(
        "Error",
        `Cannot refund more than ${currency} ${ledgerTotals.total_payments}`,
        "error",
      );
    }
    if (!refundReason.trim()) {
      return Swal.fire("Error", "Reason is required.", "error");
    }
    if (!refundReceiptFile) {
      return Swal.fire("Error", "Refund receipt is required", "error");
    }
    try {
      setLoadingRefund(true);
      const formData = new FormData();
      formData.append("amount", amount);
      formData.append("reason", refundReason);
      formData.append("refund_date", refundDate);
      formData.append("receipt", refundReceiptFile);
      formData.append("method", refundMethod);
      const application_type = type === "mig" ? "migration" : "education";
      const response = await apiUrl.post(
        `refund-payment/${application_type}/${selectedApplication.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (response.data.status) {
        Swal.fire("Refunded!", "has been refunded.", "success");
        setRefundAmount("");
        setRefundReason("");
        setRefundDate(new Date().toISOString().split("T")[0]);
        setRefundReceiptFile(null);
        setRefundReceiptPreview("");
        if (refundReceiptPreview) URL.revokeObjectURL(refundReceiptPreview);
        await refreshDataAfterChange();
        setShowRefundModal(false);
      } else {
        Swal.fire("Error", response.data.error || "Failed to refund", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong.", "error");
    } finally {
      setLoadingRefund(false);
    }
  };

  const removeReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview("");
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length) {
      handleReceiptChange(files[0]);
    }
  };

  const handleRefundReceiptChange = (file) => {
    if (!file) return;
    const validTypes = ["image/", "application/pdf"];
    const isValid = validTypes.some((type) => file.type.startsWith(type));
    if (!isValid) {
      return Swal.fire("Invalid File", "Only images or PDFs allowed.", "info");
    }
    setRefundReceiptFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setRefundReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setRefundReceiptPreview("");
    }
  };

  const handleReceiptChange = (file) => {
    if (!file) return;
    if (!isAllowedReceiptFile(file)) {
      return Swal.fire("Invalid File", "Only images or PDF allowed.", "info");
    }
    setReceiptFile(file);
    if (isReceiptImageFile(file)) {
      const reader = new FileReader();
      reader.onload = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(buildReceiptPreviewUrl(file));
    }
  };

  const getApplicationPaymentPlan = async () => {
    try {
      setLoadingPaymentInstallments(true);
      const application_type = type === "mig" ? "migration" : "education";
      const application_id = selectedApplication?.id;
      if (!application_type || !application_id) {
        console.error("Missing application type or ID");
        return;
      }
      const response = await apiUrl.get(
        `getInstallmentPlan/${application_type}/${application_id}`,
      );
      if (response?.data?.status) {
        setActivePlan(response?.data?.data);
      }
    } catch (error) {
      console.error("No payment plan found:", error);
    } finally {
      setLoadingPaymentInstallments(false);
    }
  };

  const fetchApplicationCostDetail = async () => {
    const application_id = selectedApplication?.id;
    if (!application_id) return;

    const typeCode = type === "mig" ? 1 : 2;

    try {
      setLoadingCostDetail(true);
      const response = await apiUrl.get(
        `/getApplicationDetail/${application_id}/${typeCode}`,
      );
      if (response?.data?.status) {
        setApplicationCostDetail(response.data.data);
      } else {
        setApplicationCostDetail(null);
      }
    } catch (error) {
      console.error("Error fetching application cost detail", error);
      setApplicationCostDetail(null);
    } finally {
      setLoadingCostDetail(false);
    }
  };

  const fetchApplicationLedgerDetail = async () => {
    try {
      setLoadingInitials(true);
      const application_type = type === "mig" ? "migration" : "education";
      const application_id = selectedApplication?.id;
      if (!application_type || !application_id) {
        console.error("Missing application type or ID");
        return;
      }
      const response = await apiUrl.get(
        `applications/${application_type}/${application_id}/ledger`,
      );
      if (response?.data?.status) {
        setApplicationLedgerDetails(response.data.data.entries);
        setLedgerTotals(response.data.data.totals);
      } else {
        console.error("No ledger entries found:", response.data.message);
        setApplicationLedgerDetails([]);
        setLedgerTotals({
          total_charges: 0,
          total_discounts: 0,
          total_payments: 0,
          remaining_balance: 0,
          total_credits: 0,
        });
      }
    } catch (error) {
      console.error("Error while fetching application records", error);
    } finally {
      setLoadingInitials(false);
    }
  };

  const updateExistingPlan = async () => {
    if (!activePlan?.id) return;
    const trimmedReason = reasonForChangePlan?.trim();
    if (!trimmedReason) {
      Swal.fire(
        "Error",
        "Please provide a valid reason for changing the plan.",
        "warning",
      );
      return;
    }
    const total = customPlanRows.reduce(
      (sum, r) => sum + Number(r.amount || 0),
      0,
    );
    const remaining = ledgerTotals?.remaining_balance;
    if (planFrequency === "custom" && Math.abs(total - remaining) > 0.009) {
      return Swal.fire(
        "Error",
        `Total must match remaining: ${currency} ${fmt(remaining)}`,
        "error",
      );
    }
    const installments = customPlanRows.map((row, i) => ({
      amount: parseFloat(row.amount),
      due_date: row.date,
    }));
    if (planFrequency === "custom") {
      const firstDate = getFirstInstallmentDate(customPlanRows);
      if (!firstDate) {
        return Swal.fire("Error", "Invalid installment dates.", "error");
      }
    }
    const data = {
      frequency: planFrequency,
      start_date: planStartDate,
      remaining_amount: remaining,
      application_id: selectedApplication.id,
      type: type === "mig" ? "migration" : "education",
      notes: reasonForChangePlan,
    };
    if (planFrequency !== "custom") {
      data.installment_count = planInstallmentsCount;
    } else {
      data.installments = customPlanRows.map((r) => ({
        amount: Number(r.amount),
        due_date: r.date,
      }));
    }
    try {
      setLoadingInstallment(true);
      const response = await apiUrl.post(
        `updateInstallmentPlan/${data.type}/${activePlan.id}`,
        data,
      );
      if (response.data.status) {
        Swal.fire("Updated!", "Payment plan has been updated.", "success");
        setEditingPlan(false);
        await getApplicationPaymentPlan();
      }
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.error, "error");
    } finally {
      setLoadingInstallment(false);
    }
  };

  const deletePlanWithInstallments = async () => {
    const plan_id = activePlan?.id;
    const application_type = type === "mig" ? "migration" : "education";
    if (!plan_id) {
      console.warn("No plan to delete");
      return;
    }
    try {
      const willDelete = await Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the plan with all its installments.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!willDelete.isConfirmed) return;
      setLoadingDeletePlan(true);
      const response = await apiUrl.post(
        `deletePlanWithInstallments/${application_type}/${plan_id}`,
      );
      if (response.data.status) {
        Swal.fire("Deleted!", response.data.message, "success");
        setActivePlan(null);
      } else {
        Swal.fire(
          "Error",
          response.data.message || "Failed to delete plan",
          "error",
        );
      }
    } catch (error) {
      console.error("Failed to delete the plan with its installments", error);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoadingDeletePlan(false);
    }
  };

  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    const getAllDiscountRules = async () => {
      try {
        const response = await apiUrl.get("getAllDiscountRules");
        if (mounted.current && response?.data?.status) {
          setDiscountFeeRules(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching discount rules:", error);
        Swal.fire("Error", "Failed to load discount rules.", "error");
      }
    };
    getAllDiscountRules();
  }, []);

  const currency = selectedApplication?.currency || "AUD";
  const fmt = (n) => (Number(n) || 0).toFixed(2);

  const applicationCostRecord = React.useMemo(
    () => ({
      ...selectedApplication,
      ...applicationCostDetail,
      currency:
        selectedApplication?.currency || applicationCostDetail?.currency || "AUD",
      gst_status:
        applicationCostDetail?.gst_status ||
        selectedApplication?.gst_status ||
        selectedApplication?.tax ||
        "",
    }),
    [selectedApplication, applicationCostDetail],
  );

  const gstDetailsMissing = isApplicationGstDetailsMissing(applicationCostRecord);

  const suggestedBaseCost =
    applicationCostRecord.base_cost ||
    selectedApplication?.professional_cost ||
    ledgerTotals?.total_charges ||
    "";

  const applicationGstInfo = React.useMemo(() => {
    const exclusive = Number(ledgerTotals?.total_gst_exclusive || 0);
    const inclusive = Number(ledgerTotals?.total_gst_inclusive || 0);
    const gstEntry = (applicationLedgerDetails || []).find(
      (r) => r.type === "GST" || r.type === "gst",
    );
    const gstType = resolveGstModeType({
      gstStatus:
        applicationCostDetail?.gst_status ||
        selectedApplication?.gst_status ||
        selectedApplication?.tax,
      ledgerExclusive: exclusive,
      ledgerInclusive: inclusive,
      gstEntryType: gstEntry?.metadata?.gst_type,
    });
    const percentage =
      Number(applicationCostDetail?.gst_percentage) ||
      Number(selectedApplication?.gst_percentage) ||
      Number(gstEntry?.metadata?.gst_percentage) ||
      0;
    const baseCost = Number(applicationCostDetail?.base_cost || 0);
    const totalCharges = Number(ledgerTotals?.total_charges || 0);
    let resolvedMode =
      gstType === "exclusive" || gstType === "inclusive" ? gstType : "";

    let amount =
      Number(applicationCostDetail?.gst_amount) ||
      Number(selectedApplication?.gst_amount) ||
      (gstType === "exclusive"
        ? exclusive
        : gstType === "inclusive"
          ? inclusive
          : exclusive || inclusive || 0);

    if (!resolvedMode && amount > 0) {
      const subtotalForInfer =
        baseCost > 0 ? baseCost : Math.max(0, totalCharges - amount);
      resolvedMode =
        inferGstModeFromAmounts({
          gstAmount: amount,
          subtotal: subtotalForInfer,
          totalAmount: totalCharges,
        }) || "notax";
    }

    if (!resolvedMode) {
      resolvedMode = "notax";
    }

    const effectiveBaseCost =
      baseCost > 0
        ? baseCost
        : resolvedMode === "inclusive"
          ? Number(
              applicationCostDetail?.professional_cost ||
                selectedApplication?.professional_cost ||
                0,
            )
          : 0;

    if (
      effectiveBaseCost > 0 &&
      percentage > 0 &&
      (resolvedMode === "inclusive" || resolvedMode === "exclusive")
    ) {
      amount = calculateGstBreakdown({
        baseCost: effectiveBaseCost,
        gstPercentage: percentage,
        taxType: resolvedMode,
      }).gst_amount;
    }

    return {
      type: resolvedMode,
      gstModeLabel: formatGstModeLabel(resolvedMode),
      percentage,
      amount,
      baseCost: effectiveBaseCost || baseCost,
      ledgerExclusive: exclusive,
      ledgerInclusive: inclusive,
      gstEntryType: gstEntry?.metadata?.gst_type || "",
    };
  }, [
    applicationCostDetail,
    selectedApplication,
    ledgerTotals,
    applicationLedgerDetails,
  ]);

  const departmentFeeCurrency =
    applicationCostDetail?.department_fee_currency ||
    selectedApplication?.department_fee_currency ||
    currency;

  const departmentGstInfo = React.useMemo(() => {
    if (type !== "mig") return null;

    const detail = applicationCostDetail || {};
    const app = selectedApplication || {};
    const totalFee = Number(detail.department_fee ?? app.department_fee ?? 0);
    const percentage = Number(detail.department_fee_gst_percentage ?? 0);
    let gstAmount = Number(detail.department_fee_gst_amount ?? 0);
    const gstStatus = String(
      detail.department_fee_gst_status ?? detail.department_gst_status ?? "",
    )
      .trim()
      .toLowerCase();

    let baseCost = Number(
      detail.department_base_fee ??
        detail.department_fee_base_cost ??
        detail.department_base_cost ??
        0,
    );

    let resolvedMode =
      gstStatus === "exclusive" || gstStatus === "inclusive" ? gstStatus : "";

    if (!baseCost && totalFee > 0) {
      if (resolvedMode === "exclusive" && gstAmount > 0) {
        baseCost = totalFee - gstAmount;
      } else if (resolvedMode === "inclusive") {
        baseCost = totalFee;
      } else if (gstAmount > 0) {
        baseCost = totalFee - gstAmount;
      } else {
        baseCost = totalFee;
      }
    }

    if (!resolvedMode && gstAmount > 0 && baseCost > 0) {
      resolvedMode =
        inferGstModeFromAmounts({
          gstAmount,
          subtotal: baseCost,
          totalAmount: totalFee || baseCost + gstAmount,
        }) || "notax";
    }

    if (!resolvedMode) {
      resolvedMode = percentage > 0 && gstAmount > 0 ? "exclusive" : "notax";
    }

    if (
      baseCost > 0 &&
      percentage > 0 &&
      (resolvedMode === "inclusive" || resolvedMode === "exclusive") &&
      !gstAmount
    ) {
      gstAmount = calculateGstBreakdown({
        baseCost,
        gstPercentage: percentage,
        taxType: resolvedMode,
      }).gst_amount;
    }

    return {
      type: resolvedMode,
      percentage,
      amount: gstAmount,
      baseCost,
      totalFee,
    };
  }, [type, applicationCostDetail, selectedApplication]);

  const reversedIds = React.useMemo(() => {
    const ids = new Set();
    (applicationLedgerDetails || []).forEach((e) => {
      if (e.reverses_entry_id) ids.add(e.reverses_entry_id);
    });
    return ids;
  }, [applicationLedgerDetails]);

  const getLedgerType = React.useCallback(
    (record) => String(record?.type || "").trim().toLowerCase(),
    [],
  );

  const paymentsNotReversed = React.useMemo(() => {
    return (applicationLedgerDetails || []).filter(
      (r) => getLedgerType(r) === "payment" && !reversedIds.has(r.id),
    );
  }, [applicationLedgerDetails, reversedIds, getLedgerType]);

  const discountsNotReversed = React.useMemo(() => {
    return (applicationLedgerDetails || []).filter(
      (r) => getLedgerType(r) === "discount" && !reversedIds.has(r.id),
    );
  }, [applicationLedgerDetails, reversedIds, getLedgerType]);

  const buildInvoicePayload = () => {
    const dueAmount = Number(ledgerTotals?.remaining_balance || 0);
    const totalAmount = Number(ledgerTotals?.total_charges || 0);
    const paidAmount = Number(ledgerTotals?.total_payments || 0);
    const creditList = (applicationLedgerDetails || [])
      .filter(
        (r) => getLedgerType(r) === "discount" && !reversedIds.has(r.id),
      )
      .map((r) => ({
        credit_amount: Number(r.amount || 0).toFixed(2),
        credit_date: r.transaction_date,
        description: r.description || "Discount",
      }));

    return {
      description: invoiceDefaultDescription,
      reference: invoiceReference,
      payments: planDetail || [],
      appid: selectedApplication?.id,
      coursename:
        selectedApplication?.courseid === 0
          ? selectedApplication?.packagename
          : selectedApplication?.coursename,
      clientname: getClientNameFromRecord(clientData),
      clientemail: getClientEmailFromRecord(clientData),
      dueAmount,
      totalAmount,
      dueDate: invoiceDueDate || new Date().toISOString().split("T")[0],
      invoiceNo,
      currency,
      status: deriveInvoiceStatusFromPayment({
        dueAmount,
        paidAmount,
        dueDate: invoiceDueDate || new Date().toISOString().split("T")[0],
      }),
      type: type === "mig" ? 1 : type === "edu" ? 2 : "",
      paidAmount,
      costDate: invoiceCostDate,
      tax:
        (ledgerTotals?.total_gst_exclusive || 0) +
          (ledgerTotals?.total_gst_inclusive || 0) >
          0
          ? "gst"
          : "notax",
      taxRate: applicationGstInfo.percentage || 0,
      taxAmount: applicationGstInfo.amount || 0,
      gst_status: applicationGstInfo.type || "notax",
      base_cost: applicationGstInfo.baseCost || 0,
      creditList,
      clientid: selectedApplication?.clientid,
    };
  };

  const saveInvoiceForEmail = async () => {
    const res = await apiUrl.post("saveInvoice", buildInvoicePayload());
    if (res?.data?.error) {
      throw new Error(res.data.error);
    }
    const invoiceId = res?.data?.invoiceid;
    if (!invoiceId) {
      throw new Error("Invoice could not be saved.");
    }
    await getAllInvoiceForApplication();
    return invoiceId;
  };

  // Save invoice
  const saveInvoice = async (e) => {
    e?.preventDefault?.();
    try {
      const invoiceId = await saveInvoiceForEmail();
      Swal.fire(
        "Success!",
        "Invoice created.",
        "success",
      );
      if (invoiceId) {
        try {
          window?.scrollTo?.(0, 0);
        } catch { }
      }
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "Failed to save invoice.",
        "error",
      );
    }
  };

  const handleInvoiceWizardCreate = async (payload) => {
    setCreatingInvoice(true);
    try {
      let currentPayload = { ...payload };
      let lastError = "Unable to create invoice.";

      for (let attempt = 0; attempt < 6; attempt += 1) {
        const response = await apiUrl.post("finance-invoices", currentPayload);

        if (isFinanceInvoiceApiSuccess(response)) {
          Swal.fire(
            "Success",
            getFinanceInvoiceApiMessage(
              response,
              `Invoice saved as ${getInvoiceStatusLabel(payload?.status)}.`,
            ),
            "success",
          );
          setShowInvoiceCreateWizard(false);
          await Promise.all([
            refreshDataAfterChange(),
            refreshInvoiceNumberingSources(),
          ]);
          return;
        }

        lastError = getFinanceInvoiceApiMessage(
          response,
          "Unable to create invoice.",
        );

        if (
          !/already taken|already exists|duplicate/i.test(lastError) ||
          attempt >= 5
        ) {
          throw new Error(lastError);
        }

        currentPayload = {
          ...currentPayload,
          invoice_number: incrementInvoiceNumber(
            currentPayload.invoice_number,
          ),
        };
      }

      throw new Error(lastError);
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to create invoice.",
        "error",
      );
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleViewHistoryInvoice = (invoice) => {
    setSelectedHistoryInvoice(invoice);
    setShowInvoicePreviewModal(true);
  };

  const historyXeroPreviewDraft = React.useMemo(() => {
    if (!selectedHistoryInvoice) return null;

    const isFinanceInvoice = Boolean(
      selectedHistoryInvoice?.invoice_number ||
        selectedHistoryInvoice?.invoice_no ||
        selectedHistoryInvoice?.total_amount != null ||
        selectedHistoryInvoice?.line_items?.length,
    );

    if (!isFinanceInvoice) return null;

    return buildInvoiceXeroPreviewDraftFromRecord(
      normalizeFinanceInvoiceFromRecord(selectedHistoryInvoice) ||
        selectedHistoryInvoice,
      { invoiceSetting: bankDetails },
    );
  }, [selectedHistoryInvoice, bankDetails]);

  const handleEditHistoryInvoice = async (invoice) => {
    const invoiceId = invoice?.id;
    setEditingInvoice(null);
    setEditingInvoiceNotes([]);
    setShowInvoiceEditWizard(true);
    setLoadingEditInvoice(true);

    try {
      if (invoiceId) {
        const [response] = await Promise.all([
          apiUrl.get(`finance-invoices/${invoiceId}`),
          fetchInvoiceNotes(invoiceId),
        ]);

        if (isFinanceInvoiceApiSuccess(response)) {
          const fullInvoice = normalizeFinanceInvoiceRecord(response);
          if (fullInvoice) {
            setEditingInvoice(fullInvoice);
            return;
          }
        }
      }

      setEditingInvoice(
        normalizeFinanceInvoiceFromRecord(invoice) || invoice,
      );
    } catch (error) {
      console.error("Failed to load invoice for editing", error);
      setEditingInvoice(
        normalizeFinanceInvoiceFromRecord(invoice) || invoice,
      );
    } finally {
      setLoadingEditInvoice(false);
    }
  };

  const handleInvoiceWizardUpdate = async ({ id, payload }) => {
    if (!id) {
      Swal.fire("Error", "Invoice could not be updated.", "error");
      return false;
    }

    setUpdatingInvoice(true);
    try {
      const response = await apiUrl.post("finance-invoices", {
        ...payload,
        id,
      });

      if (!isFinanceInvoiceApiSuccess(response)) {
        throw new Error(
          getFinanceInvoiceApiMessage(
            response,
            "Unable to update invoice.",
          ),
        );
      }

      Swal.fire(
        "Success",
        getFinanceInvoiceApiMessage(
          response,
          `Invoice updated as ${getInvoiceStatusLabel(payload?.status)}.`,
        ),
        "success",
      );

      setShowInvoiceEditWizard(false);
      setEditingInvoice(null);

      await getAllInvoiceForApplication();
      await refreshDataAfterChange();
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to update invoice.",
        "error",
      );
      return false;
    } finally {
      setUpdatingInvoice(false);
    }
  };

  const handleUpdateInvoiceDiscount = async (payload = {}) => {
    const {
      invoice_id: invoiceId,
      invoice_item_id: invoiceItemId,
      discount_transaction_id: discountTransactionId,
      discount_reason: discountReason,
      discount_price: discountPrice,
      type: discountType,
      percentage = null,
      fixed_amount = null,
      description = "",
    } = payload;

    const isDiscountUpdate = Boolean(discountTransactionId);

    if (!invoiceId || !invoiceItemId) {
      Swal.fire("Error", "Invoice discount could not be updated.", "error");
      return false;
    }

    if (isDiscountUpdate && (!discountReason?.trim() || Number(discountPrice) < 0 || Number.isNaN(Number(discountPrice)))) {
      Swal.fire("Error", "Discount amount and reason are required.", "error");
      return false;
    }

    if (!selectedApplication?.id) {
      Swal.fire("Error", "Application not found.", "error");
      return false;
    }

    setUpdatingInvoiceDiscount(true);
    try {
      const application_type = type === "mig" ? "migration" : "education";
      const response = await apiUrl.post(
        isDiscountUpdate
          ? `update-discount/${application_type}/${selectedApplication.id}`
          : `apply-discount/${application_type}/${selectedApplication.id}`,
        isDiscountUpdate
          ? {
              invoice_id: invoiceId,
              invoice_item_id: invoiceItemId,
              discount_transaction_id: discountTransactionId,
              discount_reason: discountReason,
              discount_price: discountPrice,
            }
          : {
              type: discountType,
              percentage,
              fixed_amount,
              description,
              invoice_id: invoiceId,
              invoice_item_id: invoiceItemId,
            },
      );

      if (!response?.data?.status) {
        throw new Error(
          response?.data?.error ||
            response?.data?.message ||
            (isDiscountUpdate
              ? "Unable to update invoice discount."
              : "Unable to apply invoice discount."),
        );
      }

      Swal.fire(
        "Success",
        isDiscountUpdate ? "Invoice discount updated." : "Invoice discount applied.",
        "success",
      );

      await refreshEditingInvoiceContext(invoiceId);

      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to update invoice discount.",
        "error",
      );
      return false;
    } finally {
      setUpdatingInvoiceDiscount(false);
    }
  };

  const createAutoPlan = async () => {
    let data = {
      frequency: planFrequency,
      installment_count: planInstallmentsCount,
      start_date: planStartDate,
      remaining_amount: ledgerTotals?.remaining_balance,
      application_id: selectedApplication.id,
      type: type === "mig" ? "migration" : "education",
    };
    try {
      setLoadingInstallment(true);
      const res = await apiUrl.post("createInstallmentPlan", data);
      if (res?.data?.status) {
        getApplicationPaymentPlan();
      }
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.error, "error");
    } finally {
      setLoadingInstallment(false);
    }
  };

  const getFirstInstallmentDate = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const dates = rows
      .map((r) => r.date)
      .filter((date) => date)
      .map((dateStr) => new Date(dateStr).getTime())
      .filter((time) => !isNaN(time));
    const earliestTime = Math.min(...dates);
    return new Date(earliestTime).toISOString().split("T")[0];
  };

  const createCustomPlan = async () => {
    if (activePlan) {
      return Swal.fire("Info", "Delete current plan first.", "info");
    }
    if (customPlanRows.length === 0) {
      return Swal.fire("Warning", "Add at least one installment.", "warning");
    }
    const total = customPlanRows.reduce(
      (sum, r) => sum + Number(r.amount || 0),
      0,
    );
    const diff = Math.abs(total - ledgerTotals?.remaining_balance);
    if (diff > 0.009) {
      return Swal.fire(
        "Warning",
        `Total must match remaining: ${currency} ${fmt(ledgerTotals?.remaining_balance)}`,
        "warning",
      );
    }
    const installments = customPlanRows.map((row, i) => ({
      amount: parseFloat(row.amount),
      due_date: row.date,
    }));
    const firstDate = getFirstInstallmentDate(customPlanRows);
    if (!firstDate) {
      return Swal.fire("Error", "Invalid installment dates.", "error");
    }
    const data = {
      application_id: selectedApplication.id,
      type: type === "mig" ? "migration" : "education",
      remaining_amount: ledgerTotals?.remaining_balance,
      frequency: "custom",
      start_date: firstDate,
      installment_count: installments?.length,
      installments: installments,
    };
    try {
      setLoadingInstallment(true);
      const response = await apiUrl.post("createInstallmentPlan", data);
      if (response?.data?.status) {
        Swal.fire("Success!", "Custom payment plan saved.", "success");
        setCustomPlanRows([]);
        await getApplicationPaymentPlan();
      }
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.error, "error");
    } finally {
      setLoadingInstallment(false);
    }
  };

  const addCustomRow = () => {
    if (ledgerTotals?.remaining_balance <= 0) {
      Swal.fire(
        "Warning",
        "No remaining amount to create installments.",
        "warning",
      );
      return;
    }
    setCustomPlanRows((s) => [
      ...s,
      { id: `c-${Date.now()}-${s.length}`, date: planStartDate, amount: "" },
    ]);
  };

  const removeCustomRow = (id) =>
    setCustomPlanRows((s) => s.filter((r) => r.id !== id));

  const updateCustomRow = (id, key, value) =>
    setCustomPlanRows((s) =>
      s.map((r) => (r.id === id ? { ...r, [key]: value } : r)),
    );

  const handlePaymentChange = (field, value) => {
    setPayment((prev) => ({ ...prev, [field]: value }));
  };

  const resetPaymentForm = () => {
    setPayment({
      amount: "",
      payment_date: new Date().toISOString().split("T")[0],
      method: "cash",
    });
    removeReceipt();
  };

  const refreshDataAfterChange = async () => {
    await getApplicationPaymentPlan();
    await fetchApplicationLedgerDetail();
    await fetchApplicationCostDetail();
    await fetchAvailableCredits();
  };

  const fetchInvoiceNotes = async (invoiceId) => {
    if (!invoiceId) {
      setEditingInvoiceNotes([]);
      return [];
    }

    try {
      setLoadingInvoiceNotes(true);
      const response = await apiUrl.get(`getInvoiceNotes/${invoiceId}`);
      if (isInvoiceNotesApiSuccess(response)) {
        const notes = normalizeInvoiceNotesList(response);
        setEditingInvoiceNotes(notes);
        return notes;
      }

      setEditingInvoiceNotes([]);
      return [];
    } catch (error) {
      console.error("Failed to load invoice notes", error);
      setEditingInvoiceNotes([]);
      return [];
    } finally {
      setLoadingInvoiceNotes(false);
    }
  };

  const refreshEditingInvoice = async (invoiceId = editingInvoice?.id) => {
    if (!invoiceId) return null;

    try {
      const invoiceResponse = await apiUrl.get(`finance-invoices/${invoiceId}`);
      if (isFinanceInvoiceApiSuccess(invoiceResponse)) {
        const refreshedInvoice = normalizeFinanceInvoiceRecord(invoiceResponse);
        if (refreshedInvoice) {
          setEditingInvoice(refreshedInvoice);
          return refreshedInvoice;
        }
      }
    } catch (error) {
      console.error("Failed to refresh invoice", error);
    }

    return null;
  };

  const refreshEditingInvoiceContext = async (invoiceId = editingInvoice?.id) => {
    if (invoiceId) {
      await Promise.all([
        refreshEditingInvoice(invoiceId),
        fetchInvoiceNotes(invoiceId),
        refreshDataAfterChange(),
        getAllInvoiceForApplication(),
      ]);
      return;
    }

    await Promise.all([refreshDataAfterChange(), getAllInvoiceForApplication()]);
  };

  const addInvoiceNote = async (noteText) => {
    const details = String(noteText || "").trim();
    if (!details) {
      Swal.fire("Warning", "Please enter a note.", "warning");
      return false;
    }

    const invoiceId = editingInvoice?.id;
    const invoiceNumber =
      editingInvoice?.invoice_number ||
      editingInvoice?.invoice_no ||
      editingInvoice?.invoiceNo;

    if (!invoiceId || !invoiceNumber) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setAddingInvoiceNote(true);
      const response = await apiUrl.post("addnoteforinvoice", {
        invoice_id: invoiceId,
        invoice_number: invoiceNumber,
        details,
      });

      if (response?.data?.success || response?.data?.status) {
        await fetchInvoiceNotes(invoiceId);
        return true;
      }

      Swal.fire(
        "Error",
        response?.data?.error ||
          response?.data?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong while adding note.",
        "error",
      );
      return false;
    } finally {
      setAddingInvoiceNote(false);
    }
  };

  const reversePayment = async (ledgerEntry) => {
    const { value: reason } = await Swal.fire({
      title: "Why are you reversing this payment?",
      input: "textarea",
      inputLabel: "Reason for reversal.",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return "Reason is required";
      },
    });
    if (!reason) return;
    try {
      setLoadingPayment(true);
      const application_type = type === "mig" ? "migration" : "education";
      const invoiceId =
        getLedgerInvoiceId(ledgerEntry) ?? editingInvoice?.id ?? null;
      const response = await apiUrl.post(
        `reverse-payment/${application_type}/${ledgerEntry.id}`,
        {
          reason,
          description: ledgerEntry.description,
          ...(invoiceId ? { invoice_id: invoiceId } : {}),
        },
      );
      if (response.data.status) {
        Swal.fire("Reversed!", "The payment has been reversed.", "success");
        await refreshEditingInvoiceContext(invoiceId);
      } else {
        Swal.fire("Error", response.data.error || "Failed to reverse", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong.", "error");
    } finally {
      setLoadingPayment(false);
    }
  };

  const addPayment = async ({ invoiceId = null } = {}) => {
    if (!payment.payment_date) {
      Swal.fire("warning", "Payment paid date is missing", "warning");
      return false;
    }
    try {
      setLoadingPayment(true);
      const formData = new FormData();
      formData.append("appid", selectedApplication.id);
      formData.append("type", type === "mig" ? "migration" : "education");
      formData.append("currency", selectedApplication.currency);
      formData.append("clientId", clientData?.id);
      if (invoiceId) {
        formData.append("invoice_id", invoiceId);
      }
      if (payment.method === "overpayment_credit") {
        formData.append("use_credit", "1");
        formData.append("amount", "0");
        formData.append("method", "credit_application");
        formData.append("payment_date", payment.payment_date);
        formData.append("plan_id", activePlan?.id || "");
      } else {
        formData.append("amount", payment.amount);
        formData.append("method", payment.method);
        formData.append("payment_date", payment.payment_date);
        formData.append("plan_id", activePlan?.id || "");
        if (receiptFile) {
          formData.append("receipt", receiptFile);
        }
      }
      const response = await apiUrl.post("addClientPayment", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status) {
        Swal.fire("Success!", "Payment recorded successfully.", "success");
        setPayment({
          amount: "",
          payment_date: new Date().toISOString().split("T")[0],
          method: "cash",
        });
        setReceiptFile(null);
        setReceiptPreview("");
        if (receiptPreview) URL.revokeObjectURL(receiptPreview);
        await refreshEditingInvoiceContext(
          invoiceId && editingInvoice?.id === invoiceId ? invoiceId : null,
        );
        return true;
      }

      Swal.fire(
        "Error",
        response.data.error || "Failed to record payment.",
        "error",
      );
      return false;
    } catch (error) {
      Swal.fire(
        "Error",
        "Something went wrong while recording payment.",
        "error",
      );
      console.log(error);
      return false;
    } finally {
      setLoadingPayment(false);
    }
  };

  const cardStyle = {
    background: "#ffffff",
    border: "1px solid #e6e9ee",
    borderRadius: 10,
    padding: 16,
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{
          display: "block",
          backgroundColor: "rgba(0,0,0,0.45)",
          zIndex: 1040,
        }}
        onClick={onClose}
      />
      <div
        className="modal fade show d-block credits-payments-modal"
        tabIndex={-1}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable credits-payments-modal__dialog">
          <div className="modal-content border-0 shadow-lg overflow-hidden credits-payments-modal__content">
            <div className="invoice-wizard-header invoice-wizard-header--xero credits-payments-modal__header">
              <div className="invoice-wizard-header__main">
                <div className="invoice-wizard-header__breadcrumb">
                  Finance · Application
                </div>
                <div className="invoice-wizard-header__title-row">
                  <h5 className="mb-0 fw-bold invoice-wizard-header__title">
                    Credits & Payments
                  </h5>
                </div>
              </div>
              <div className="invoice-wizard-header__actions">
                <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                />
              </div>
            </div>
            <div className="modal-body credits-payments-modal__body">
              <div className="credits-payments-modal__contact-bar">
                <div className="credits-payments-modal__contact-field">
                  <span className="credits-payments-modal__contact-label">
                    Contact
                  </span>
                  <div className="credits-payments-modal__contact-client">
                    <span className="credits-payments-modal__contact-avatar">
                      {getClientInitials(
                        getClientNameFromRecord(clientData) || "",
                      )}
                    </span>
                    <span className="credits-payments-modal__contact-name">
                      {getClientNameFromRecord(clientData) || "—"}
                    </span>
                  </div>
                </div>
                <div className="credits-payments-modal__contact-field">
                  <span className="credits-payments-modal__contact-label">
                    Application
                  </span>
                  <div className="credits-payments-modal__contact-value">
                    {selectedApplication?.coursename ||
                      selectedApplication?.course?.name ||
                      selectedApplication?.coursetype ||
                      "—"}
                  </div>
                </div>
                <div className="credits-payments-modal__contact-field">
                  <span className="credits-payments-modal__contact-label">
                    Reference
                  </span>
                  <div className="credits-payments-modal__contact-meta">
                    App ID {selectedApplication?.id || "—"}
                  </div>
                  <div className="credits-payments-modal__contact-meta">
                    Client {selectedApplication?.clientid || "—"}
                  </div>
                </div>
              </div>

              <div className="credits-payments-modal__stack">
              {loadingInitials || loadingCostDetail ? (
                <FinancialSummarySkeleton />
              ) : (
                <FinancialSummary
                  currency={currency}
                  fmt={fmt}
                  applicationGstInfo={applicationGstInfo}
                  departmentGstInfo={departmentGstInfo}
                  ledgerTotals={ledgerTotals}
                  isMigration={type === "mig"}
                  departmentFeeCurrency={departmentFeeCurrency}
                />
              )}
              {!loadingInitials &&
                !loadingCostDetail &&
                gstDetailsMissing && (
                  <div className="gst-setup-banner">
                    <div className="gst-setup-banner__text">
                      <div className="gst-setup-banner__title">
                        GST details not configured
                      </div>
                      This application is missing GST details. Set them here
                      (same as edit application) so the financial summary and
                      invoice show the correct GST breakdown.
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-warning gst-setup-banner__btn"
                      onClick={() => setShowGstSetupModal(true)}
                    >
                      <FaPenAlt className="me-1" /> Configure GST
                    </button>
                  </div>
                )}
              <div className="credits-payments-modal__ledger-plan-stack">
                  {/* Ledger */}
                  <div className="credits-payments-section">
                    <div className="credits-payments-section__header">
                      <h6 className="credits-payments-section__title">Ledger</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary btn-refund-outline"
                        disabled={!ledgerTotals?.total_payments > 0}
                        onClick={() => {
                          setShowRefundModal(true);
                          setRefundAmount("");
                          setRefundReason("");
                          setRefundDate(new Date().toISOString().split("T")[0]);
                          setRefundReceiptFile(null);
                          setRefundReceiptPreview("");
                        }}
                      >
                        <FaCoins className="me-1" /> Refund Payment
                      </button>
                    </div>
                    <div className="credits-payments-section__body">
                    <div className="credits-payments-ledger-table-wrap">
                      <table
                        className="table table-sm align-middle credits-payments-ledger-table"
                      >
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Invoice #</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Transaction Date</th>
                            <th>Description</th>
                            <th>Recorded By</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        {loadingInitials ? (
                          <TableSkeleton rows={5} />
                        ) : (
                          <tbody>
                            {applicationLedgerDetails.length > 0 ? (
                              applicationLedgerDetails.map((record) => (
                                <tr key={record.id}>
                                  <td>{record.id}</td>
                                  <td className="text-muted fw-semibold">
                                    {getLedgerInvoiceNumber(
                                      record,
                                      applicationInvoices,
                                      applicationLedgerDetails,
                                    ) ?? "—"}
                                  </td>
                                  <td>
                                    {record.type === "credit" ? (
                                      <span
                                        className="badge bg-success"
                                        style={{
                                          fontSize: "12px",
                                          padding: "6px 8px",
                                          fontWeight: "600",
                                        }}
                                      >
                                        <FaCoins size={10} className="me-1" />{" "}
                                        Credit
                                      </span>
                                    ) : record.type === "GST" ? (
                                      <span
                                        className={`badge ${record.metadata?.gst_type ===
                                            "exclusive"
                                            ? "bg-warning text-dark"
                                            : "bg-info text-dark"
                                          }`}
                                        style={{
                                          fontSize: "12px",
                                          padding: "6px 8px",
                                          fontWeight: "600",
                                        }}
                                      >
                                        GST (
                                        {record.metadata?.gst_type || "___"})
                                      </span>
                                    ) : (
                                      <span
                                        style={{
                                          color:
                                            record.type === "charge"
                                              ? "#0d6efd"
                                              : record.type === "payment" ||
                                                record.type === "reversal"
                                                ? "#059669"
                                                : record.type === "discount" ||
                                                  record.type ===
                                                  "discount_reversal"
                                                  ? "#b91c1c"
                                                  : record.type === "credit" ||
                                                    record.type ===
                                                    "credit_refund"
                                                    ? "#16a34a"
                                                    : "inherit",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {record.type || "___"}
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      color:
                                        record.type === "charge"
                                          ? "#0d6efd"
                                          : record.type === "payment" ||
                                            record.type === "reversal"
                                            ? "#059669"
                                            : record.type === "discount" ||
                                              record.type ===
                                              "discount_reversal"
                                              ? "#b91c1c"
                                              : record.type === "credit" ||
                                                record.type ===
                                                "credit_refund"
                                                ? "#16a34a"
                                                : "inherit",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {currency}{" "}
                                    {parseFloat(record.amount).toFixed(2)}
                                  </td>
                                  <td>
                                    {moment(record.transaction_date).format(
                                      "MMM DD, YYYY",
                                    ) || "___"}
                                  </td>
                                  <td>{record.description || "___"}</td>
                                  <td className="text-muted">
                                    {record.user?.name || "___"}
                                  </td>
                                  <td>
                                    {record.type === "reversal" ||
                                      record.type === "discount_reversal" ? (
                                      <span
                                        className={`badge d-inline-flex align-items-center ${record.type === "reversal"
                                            ? "bg-success text-white"
                                            : "bg-warning text-dark"
                                          }`}
                                        style={{
                                          fontSize: "12px",
                                          padding: "6px 8px",
                                          gap: "6px",
                                        }}
                                        title={`Reverses ID: ${record.reverses_entry_id}`}
                                      >
                                        {record.type === "reversal" ? (
                                          <FaDollarSign size={10} />
                                        ) : (
                                          <FaTag size={10} />
                                        )}
                                        {record.type === "reversal"
                                          ? "Rev: Payment"
                                          : "Rev: Discount"}
                                      </span>
                                    ) : applicationLedgerDetails.some(
                                      (e) =>
                                        e.reverses_entry_id === record.id,
                                    ) ? (
                                      <span
                                        className="badge bg-secondary d-inline-flex align-items-center"
                                        style={{
                                          fontSize: "12px",
                                          padding: "6px 8px",
                                          gap: "6px",
                                        }}
                                        title="This transaction has been reversed"
                                      >
                                        <FaBan size={10} /> Reversed
                                      </span>
                                    ) : (
                                      <div className="d-flex gap-1">
                                        {record.type === "payment" &&
                                          record.receipt_url && (
                                            <button
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() =>
                                                window.open(
                                                  record.receipt_url,
                                                  "_blank",
                                                )
                                              }
                                              title="View Receipt"
                                              style={{ padding: "6px 8px" }}
                                            >
                                              <FaEye size={14} />
                                            </button>
                                          )}
                                        {record.type === "payment_refund" && (
                                          <span
                                            className="badge bg-dark text-white d-inline-flex align-items-center"
                                            style={{
                                              fontSize: "12px",
                                              padding: "6px 8px",
                                              gap: "6px",
                                            }}
                                            title="This transaction has been reversed"
                                          >
                                            <FaBan size={10} /> Refund
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {record.type === "credit_refund" &&
                                      record.receipt_url && (
                                        <button
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() =>
                                            window.open(
                                              record.receipt_url,
                                              "_blank",
                                            )
                                          }
                                          title="View Refund Receipt"
                                        >
                                          <FaEye size={14} />
                                        </button>
                                      )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-muted py-3"
                                >
                                  No transaction history available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        )}
                      </table>
                    </div>
                    </div>
                  </div>
                  {/* Payment Plan Section */}
                  <div className="credits-payments-section">
                    {loadingPaymentInstallments ? (
                      <>
                        <div className="credits-payments-section__header">
                          <h6 className="credits-payments-section__title">
                            Payment Plan
                          </h6>
                        </div>
                        <div className="credits-payments-section__body credits-payments-section__body--padded">
                        <PaymentPlanSkeleton />
                        </div>
                      </>
                    ) : !activePlan ? (
                      <>
                        <div className="credits-payments-section__header">
                          <h6 className="credits-payments-section__title">
                            Create Payment Installments
                          </h6>
                          {ledgerTotals?.remaining_balance <= 0 && (
                            <small
                              style={{ color: "#dc3545", fontSize: "12px" }}
                            >
                              No remaining amount to create installments
                            </small>
                          )}
                        </div>
                        <div className="credits-payments-section__body credits-payments-section__body--padded">
                        <div className="d-flex flex-wrap gap-3 mb-3">
                          <div style={{ flex: "1 1 45%" }}>
                            <label className="form-label">Frequency</label>
                            <select
                              value={planFrequency}
                              onChange={(e) => setPlanFrequency(e.target.value)}
                              className="form-select"
                            >
                              <option value="monthly">Monthly</option>
                              <option value="weekly">Weekly</option>
                              <option value="fortnightly">Fortnightly</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                          {planFrequency !== "custom" && (
                            <div style={{ flex: "1 1 45%" }}>
                              <label className="form-label">Start Date</label>
                              <input
                                type="date"
                                value={planStartDate}
                                onChange={(e) =>
                                  setPlanStartDate(e.target.value)
                                }
                                className="form-control"
                              />
                            </div>
                          )}
                          {planFrequency !== "custom" ? (
                            <>
                              <div style={{ flex: "1 1 45%" }}>
                                <label className="form-label">
                                  Number of Installments
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  value={planInstallmentsCount}
                                  onChange={(e) =>
                                    setPlanInstallmentsCount(
                                      Number(e.target.value),
                                    )
                                  }
                                  className="form-control"
                                  placeholder="e.g. 3"
                                />
                              </div>
                              <div
                                style={{
                                  flex: "1 1 45%",
                                  display: "flex",
                                  alignItems: "flex-end",
                                }}
                              >
                                <button
                                  className="btn btn-primary w-100"
                                  onClick={createAutoPlan}
                                  disabled={
                                    loadingInstallment ||
                                    ledgerTotals?.remaining_balance <= 0
                                  }
                                >
                                  {loadingInstallment ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      Creating...
                                    </>
                                  ) : (
                                    <>
                                      <FaCalendarCheck
                                        size={14}
                                        className="me-2"
                                      />
                                      Create Plan
                                    </>
                                  )}
                                </button>
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                flex: "1 1 100%",
                                display: "flex",
                                alignItems: "flex-end",
                                gap: "0.5rem",
                              }}
                            >
                              <button
                                className="btn btn-primary"
                                onClick={addCustomRow}
                                disabled={ledgerTotals?.remaining_balance <= 0}
                              >
                                <FiPlus style={{ marginRight: 6 }} /> Add Row
                              </button>
                              <button
                                className="btn btn-success"
                                onClick={createCustomPlan}
                                disabled={
                                  loadingInstallment ||
                                  ledgerTotals?.remaining_balance <= 0
                                }
                              >
                                {loadingInstallment ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Creating...
                                  </>
                                ) : (
                                  <>
                                    <FaCalendarCheck
                                      size={14}
                                      className="me-2"
                                    />
                                    Create Plan
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                        {planFrequency === "custom" && (
                          <>
                            <div className="d-flex justify-content-between mb-2">
                              <div className="text-muted">
                                Remaining after ledger:{" "}
                                <strong>
                                  {currency}{" "}
                                  {fmt(ledgerTotals?.remaining_balance)}
                                </strong>
                              </div>
                              <div className="text-muted">
                                Custom total:{" "}
                                <strong>
                                  {currency}{" "}
                                  {fmt(
                                    customPlanRows.reduce(
                                      (s, r) => s + Number(r.amount || 0),
                                      0,
                                    ),
                                  )}
                                </strong>{" "}
                              </div>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                              <table
                                className="table table-sm align-middle"
                                style={{ marginBottom: 0 }}
                              >
                                <thead>
                                  <tr>
                                    <th style={{ width: "50px" }}>#</th>
                                    <th
                                      style={{
                                        width: "180px",
                                        textAlign: "center",
                                      }}
                                    >
                                      Due Date
                                    </th>
                                    <th
                                      style={{
                                        width: "180px",
                                        textAlign: "right",
                                      }}
                                    >
                                      Amount
                                    </th>
                                    <th
                                      style={{
                                        width: "100px",
                                        textAlign: "center",
                                      }}
                                    >
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customPlanRows.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan={4}
                                        className="text-muted text-center"
                                      >
                                        No custom rows. Click "Add Row".
                                      </td>
                                    </tr>
                                  ) : (
                                    customPlanRows.map((row, idx) => (
                                      <tr key={row.id}>
                                        <td>{idx + 1}</td>
                                        <td className="text-center">
                                          <input
                                            type="date"
                                            value={row.date}
                                            onChange={(e) =>
                                              updateCustomRow(
                                                row.id,
                                                "date",
                                                e.target.value,
                                              )
                                            }
                                            className="form-control"
                                            style={{
                                              maxWidth: 160,
                                              margin: "0 auto",
                                            }}
                                          />
                                        </td>
                                        <td className="text-end">
                                          <input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            value={row.amount}
                                            onChange={(e) =>
                                              updateCustomRow(
                                                row.id,
                                                "amount",
                                                e.target.value,
                                              )
                                            }
                                            className="form-control text-end"
                                            style={{
                                              maxWidth: 160,
                                              marginLeft: "auto",
                                            }}
                                          />
                                        </td>
                                        <td className="text-center">
                                          <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                              removeCustomRow(row.id)
                                            }
                                            title="Remove row"
                                          >
                                            <FaTrash />
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="credits-payments-section__header">
                          <h6 className="credits-payments-section__title">
                            Current Payment Plan
                          </h6>
                          <div className="d-flex gap-2">
                            {ledgerTotals?.remaining_balance <= 0 ? (
                              <small
                                style={{ color: "#dc3545", fontSize: "12px" }}
                              >
                                No remaining amount to modify plan
                              </small>
                            ) : (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                  setReasonForChangePlan("");
                                  setEditingPlan(true);
                                }}
                                disabled={
                                  loadingDeletePlan ||
                                  ledgerTotals?.remaining_balance <= 0
                                }
                              >
                                <FaPenAlt className="me-2" /> Modify Plan
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="credits-payments-section__body credits-payments-section__body--padded">
                        {editingPlan && activePlan && (
                          <div
                            style={{
                              ...cardStyle,
                              marginTop: 12,
                              background: "#f8f9fa",
                            }}
                          >
                            <div style={{ fontWeight: 700, marginBottom: 10 }}>
                              Edit Payment Plan
                            </div>
                            <div className="d-flex flex-wrap gap-3 mb-3">
                              <div style={{ flex: "1 1 45%" }}>
                                <label className="form-label">Frequency</label>
                                <select
                                  value={planFrequency}
                                  onChange={(e) =>
                                    setPlanFrequency(e.target.value)
                                  }
                                  className="form-select"
                                >
                                  <option value="monthly">Monthly</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="fortnightly">
                                    Fortnightly
                                  </option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>
                              <div style={{ flex: "1 1 45%" }}>
                                <label className="form-label">Start Date</label>
                                <input
                                  type="date"
                                  value={planStartDate}
                                  onChange={(e) =>
                                    setPlanStartDate(e.target.value)
                                  }
                                  className="form-control"
                                />
                              </div>
                              {planFrequency !== "custom" && (
                                <div style={{ flex: "1 1 45%" }}>
                                  <label className="form-label">
                                    Number of Installments
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={planInstallmentsCount}
                                    onChange={(e) =>
                                      setPlanInstallmentsCount(
                                        Number(e.target.value),
                                      )
                                    }
                                    className="form-control"
                                  />
                                </div>
                              )}
                            </div>
                            {planFrequency === "custom" && (
                              <>
                                <div className="mb-2">
                                  <button
                                    className="btn btn-sm btn-primary mb-2"
                                    onClick={addCustomRow}
                                  >
                                    <FiPlus size={12} /> Add Installment
                                  </button>
                                </div>
                                <div style={{ overflowX: "auto" }}>
                                  <table className="table table-sm">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Due Date</th>
                                        <th>Amount</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {customPlanRows.length === 0 ? (
                                        <tr>
                                          <td
                                            colSpan="4"
                                            className="text-center text-muted"
                                          >
                                            No installments. Click "Add
                                            Installment".
                                          </td>
                                        </tr>
                                      ) : (
                                        customPlanRows.map((row, idx) => (
                                          <tr key={row.id}>
                                            <td>{idx + 1}</td>
                                            <td>
                                              <input
                                                type="date"
                                                value={row.date}
                                                onChange={(e) =>
                                                  updateCustomRow(
                                                    row.id,
                                                    "date",
                                                    e.target.value,
                                                  )
                                                }
                                                className="form-control"
                                              />
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                step="0.01"
                                                value={row.amount}
                                                onChange={(e) =>
                                                  updateCustomRow(
                                                    row.id,
                                                    "amount",
                                                    e.target.value,
                                                  )
                                                }
                                                className="form-control"
                                              />
                                            </td>
                                            <td>
                                              <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() =>
                                                  removeCustomRow(row.id)
                                                }
                                              >
                                                <FaTrash />
                                              </button>
                                            </td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                            <div className="mt-2 mb-2">
                              <label htmlFor="reason" className="form-label">
                                Reason for changing plan
                              </label>
                              <textarea
                                required
                                className="form-control"
                                id="reason"
                                rows={3}
                                placeholder="Please provide details about why you are changing the plan."
                                style={{ width: "100%", padding: "8px" }}
                                onChange={(e) =>
                                  setReasonForChangePlan(e.target.value)
                                }
                              />
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-success"
                                onClick={updateExistingPlan}
                                disabled={loadingInstallment}
                              >
                                {loadingInstallment ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm" />{" "}
                                    Saving...
                                  </>
                                ) : (
                                  "Save Changes"
                                )}
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={() => setEditingPlan(false)}
                                disabled={loadingInstallment}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="mb-3 mt-3">
                          <small>
                            <strong>Frequency:</strong>{" "}
                            {activePlan?.frequency || "___"} |&nbsp;
                            <strong>Start Date:</strong>{" "}
                            {activePlan?.start_date || "___"} |&nbsp;
                            <strong>Total:</strong> {currency}{" "}
                            {fmt(activePlan?.total_amount || 0)}
                          </small>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <table
                            className="table table-sm align-middle"
                            style={{ marginBottom: 0 }}
                          >
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Due Date</th>
                                <th>Amount</th>
                                <th>Paid Amount</th>
                                <th>Remaining Amount</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activePlan?.installments?.length === 0 ||
                                !activePlan ? (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="text-muted text-center"
                                  >
                                    No installments.
                                  </td>
                                </tr>
                              ) : (
                                activePlan?.installments?.map((item) => (
                                  <tr key={item.installment_no}>
                                    <td>{item.installment_no}</td>
                                    <td>{item.due_date}</td>
                                    <td>
                                      {currency} {fmt(item.amount)}
                                    </td>
                                    <td>{item.paid_amount}</td>
                                    <td>
                                      {(item.amount - item.paid_amount).toFixed(
                                        2,
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${item.status === "paid"
                                            ? "bg-success"
                                            : item.status === "partial"
                                              ? "bg-warning text-dark"
                                              : "bg-danger"
                                          }`}
                                        style={{
                                          fontSize: "12px",
                                          padding: "6px 10px",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {item.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        {activePlan?.history.length > 0 && (
                          <div style={{ marginTop: "2rem" }}>
                            <h6
                              style={{
                                fontWeight: 600,
                                marginBottom: "0.5rem",
                              }}
                            >
                              <FaHistory size={16} className="me-2" /> Plan
                              Change History
                            </h6>
                            <div
                              style={{
                                maxHeight: "400px",
                                overflowY: "auto",
                                border: "1px solid #eef2f6",
                                borderRadius: "8px",
                              }}
                            >
                              {activePlan?.history.map((log, index) => (
                                <div
                                  key={log.id}
                                  style={{
                                    padding: "1rem",
                                    backgroundColor:
                                      index % 2 === 0 ? "#f9fafb" : "white",
                                    borderBottom: "1px solid #eef2f6",
                                    fontSize: "14px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: "0.5rem",
                                    }}
                                  >
                                    <strong style={{ color: "#1f2937" }}>
                                      Changed on{" "}
                                      {new Date(
                                        log.created_at,
                                      ).toLocaleString()}
                                    </strong>
                                    <span style={{ color: "#6b7280" }}>
                                      by {log.changed_by || "Staff"}
                                    </span>
                                  </div>
                                  {log.notes && (
                                    <div
                                      style={{
                                        marginBottom: "0.5rem",
                                        fontStyle: "italic",
                                        color: "#4b5563",
                                      }}
                                    >
                                      <strong>Reason:</strong> {log.notes}
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: "0.5rem",
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    <div>
                                      <strong>Old Total:</strong> {currency}{" "}
                                      {fmt(log.old_data.total_amount)}
                                      <br />
                                      <strong>Frequency:</strong>{" "}
                                      {log.old_data.frequency}
                                      <br />
                                      <strong>Start Date:</strong>{" "}
                                      {log.old_data.start_date}
                                    </div>
                                    <div>
                                      <strong>Installments:</strong>{" "}
                                      {log.old_data.installments.length}
                                      <br />
                                      <strong>Paid:</strong>{" "}
                                      {
                                        log.old_data.installments.filter(
                                          (i) => i.status === "paid",
                                        ).length
                                      }
                                      <br />
                                      <strong>Pending:</strong>{" "}
                                      {
                                        log.old_data.installments.filter(
                                          (i) => i.status === "pending",
                                        ).length
                                      }
                                    </div>
                                  </div>
                                  <details style={{ marginTop: "0.5rem" }}>
                                    <summary
                                      style={{
                                        cursor: "pointer",
                                        color: "#059669",
                                      }}
                                    >
                                      View Old Installments
                                    </summary>
                                    <table className="table table-sm mt-2">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Due Date</th>
                                          <th>Amount</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {log.old_data.installments.map(
                                          (inst) => (
                                            <tr key={inst.installment_no}>
                                              <td>{inst.installment_no}</td>
                                              <td>{inst.due_date}</td>
                                              <td>
                                                {currency} {fmt(inst.amount)}
                                              </td>
                                              <td>
                                                <span
                                                  className={`badge ${inst.status === "paid"
                                                      ? "bg-success"
                                                      : inst.status ===
                                                        "partial"
                                                        ? "bg-warning text-dark"
                                                        : "bg-danger"
                                                    }`}
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {inst.status}
                                                </span>
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </details>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {showRefundModal && (
                <div
                  className="modal d-block"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  onClick={onClose}
                >
                  <div
                    className="modal-dialog modal-dialog-centered modal-lg"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="modal-content border-0 rounded-4 shadow">
                      <div
                        className="modal-header"
                        style={{
                          background: "#f7f8fa",
                          borderBottom: "1px solid #eef2f6",
                          padding: "1rem 1.5rem",
                          borderTopLeftRadius: "0.5rem",
                          borderTopRightRadius: "0.5rem",
                        }}
                      >
                        <h5 className="modal-title" style={{ fontWeight: 700 }}>
                          <FaCoins className="me-2" /> Refund Overpayment
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowRefundModal(false)}
                        ></button>
                      </div>
                      <div className="modal-body p-4">
                        <div
                          style={{
                            backgroundColor: "#f0fdf4",
                            border: "1px solid #d1fae5",
                            borderRadius: "0.5rem",
                            padding: "1rem",
                            marginBottom: "1.5rem",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.875rem",
                              color: "#065f46",
                              fontWeight: 500,
                            }}
                          >
                            Refundable Amount (from Payments)
                          </div>
                          <div
                            style={{
                              fontSize: "1.5rem",
                              fontWeight: 700,
                              color: "#059669",
                              marginTop: "0.25rem",
                            }}
                          >
                            {currency} {fmt(ledgerTotals?.total_payments)}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#065f46",
                              marginTop: "0.5rem",
                              fontStyle: "italic",
                            }}
                          >
                            ⚠️ Refund applies to payments only — not overpayment
                            credits.
                          </div>
                        </div>
                        <div className="row ">
                          <div className="d-flex gap-2">
                            <div>
                              <label className="form-label">Refund Date</label>
                              <input
                                type="date"
                                className="form-control"
                                value={refundDate}
                                onChange={(e) => setRefundDate(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="form-label">
                                Refund Amount
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={refundAmount}
                                onChange={(e) =>
                                  setRefundAmount(e.target.value)
                                }
                                placeholder={`Max: ${currency} ${ledgerTotals.total_payments}`}
                                max={ledgerTotals.total_payments}
                                style={{
                                  borderColor:
                                    parseFloat(refundAmount) >
                                      ledgerTotals.total_payments
                                      ? "#dc3545"
                                      : "",
                                }}
                              />
                              {parseFloat(refundAmount) >
                                ledgerTotals.total_payments && (
                                  <small className="text-danger d-block mt-1">
                                    ⚠️ Cannot exceed total payments ({currency}{" "}
                                    {fmt(ledgerTotals.total_payments)})
                                  </small>
                                )}
                              {parseFloat(refundAmount) > 0 &&
                                parseFloat(refundAmount) <=
                                ledgerTotals.total_payments && (
                                  <small className="text-success d-block mt-1">
                                    ✓ Valid: {currency} {fmt(refundAmount)} ≤{" "}
                                    {currency}{" "}
                                    {fmt(ledgerTotals.total_payments)}
                                  </small>
                                )}
                            </div>
                          </div>
                          <div className="col-12 mt-1">
                            <label className="form-label">
                              Reason for Refund
                            </label>
                            <textarea
                              className="form-control"
                              rows="3"
                              placeholder="E.g., Client requested refund"
                              value={refundReason}
                              onChange={(e) => setRefundReason(e.target.value)}
                            ></textarea>
                          </div>
                          <div style={{ flex: "1 1 30%" }} className="mt-1">
                            <label className="form-label">Method</label>
                            <select
                              value={refundMethod}
                              onChange={(e) =>
                                setRefundMethod("method", e.target.value)
                              }
                              className="form-select"
                            >
                              <option value="cash">Cash</option>
                              <option value="bank">Bank Transfer</option>
                              <option value="card">Card</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="mb-4 mt-1">
                            <div>
                              <label className="form-label">
                                Refund Receipt
                              </label>
                              {!refundReceiptFile ? (
                                <div
                                  style={{
                                    border: "2px dashed #cbd5e1",
                                    borderRadius: "8px",
                                    padding: "40px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    transition: "border 0.2s ease",
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    const file = e.dataTransfer.files[0];
                                    handleRefundReceiptChange(file);
                                  }}
                                  onDragOver={(e) => e.preventDefault()}
                                  onClick={() =>
                                    document
                                      .getElementById("refundFileUpload")
                                      .click()
                                  }
                                >
                                  <p className="text-muted">
                                    Drag & drop or click to upload
                                  </p>
                                  <input
                                    id="refundFileUpload"
                                    type="file"
                                    accept="image/*,application/pdf"
                                    style={{ display: "none" }}
                                    onChange={(e) =>
                                      handleRefundReceiptChange(
                                        e.target.files[0],
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "12px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    backgroundColor: "#f9fafb",
                                  }}
                                >
                                  {refundReceiptPreview ? (
                                    <img
                                      src={refundReceiptPreview}
                                      alt="Selected file"
                                      style={{
                                        width: "60px",
                                        height: "60px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: "60px",
                                        height: "60px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#e5e7eb",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      PDF
                                    </div>
                                  )}
                                  <div style={{ flex: 1 }}>
                                    <p style={{ margin: 0, fontSize: "14px" }}>
                                      {refundReceiptFile.name}
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "12px",
                                        color: "#6b7280",
                                      }}
                                    >
                                      {(refundReceiptFile.size / 1024).toFixed(
                                        1,
                                      )}{" "}
                                      KB
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (refundReceiptPreview)
                                        URL.revokeObjectURL(
                                          refundReceiptPreview,
                                        );
                                      setRefundReceiptFile(null);
                                      setRefundReceiptPreview("");
                                    }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#ef4444",
                                      cursor: "pointer",
                                      fontSize: "18px",
                                    }}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="modal-footer"
                        style={{
                          background: "#f7f8fa",
                          borderTop: "1px solid #eef2f6",
                          padding: "1rem 1.5rem",
                          borderBottomLeftRadius: "0.5rem",
                          borderBottomRightRadius: "0.5rem",
                        }}
                      >
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setShowRefundModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={handleRefund}
                          disabled={
                            loadingRefund ||
                            !refundAmount ||
                            !refundReceiptFile ||
                            parseFloat(refundAmount) <= 0 ||
                            parseFloat(refundAmount) >
                            ledgerTotals.total_payments ||
                            !refundReason.trim()
                          }
                        >
                          {loadingRefund ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Processing...
                            </>
                          ) : (
                            <>
                              {" "}
                              💸 Refund {currency}{" "}
                              {fmt(parseFloat(refundAmount) || 0)}{" "}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer credits-payments-modal__footer">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm invoice-wizard-header-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      {showGstSetupModal && (
        <ApplicationGstSetupModal
          show={showGstSetupModal}
          onClose={() => setShowGstSetupModal(false)}
          onSaved={refreshDataAfterChange}
          appId={selectedApplication?.id}
          type={type}
          applicationDetails={applicationCostRecord}
          suggestedBaseCost={suggestedBaseCost}
        />
      )}
      <InvoiceCreateWizard
        show={showInvoiceCreateWizard}
        onClose={() => setShowInvoiceCreateWizard(false)}
        onCreate={handleInvoiceWizardCreate}
        application={selectedApplication}
        applicationType={type === "mig" ? "migration" : "education"}
        applicationCost={applicationCostRecord}
        applicationGstInfo={applicationGstInfo}
        departmentGstInfo={departmentGstInfo}
        clientData={clientData}
        invoiceSetting={bankDetails}
        saving={creatingInvoice}
        discountFeeRules={discountFeeRules}
        applicationInvoices={applicationInvoices}
        existingInvoicesForNumbering={invoiceNumberingInvoices}
      />
      <InvoiceCreateWizard
        show={showInvoiceEditWizard}
        editMode
        onClose={() => {
          setShowInvoiceEditWizard(false);
          setEditingInvoice(null);
          setEditingInvoiceNotes([]);
          setLoadingEditInvoice(false);
          setLoadingInvoiceNotes(false);
        }}
        onUpdate={handleInvoiceWizardUpdate}
        onUpdateDiscount={handleUpdateInvoiceDiscount}
        existingInvoice={editingInvoice}
        application={selectedApplication}
        applicationType={type === "mig" ? "migration" : "education"}
        applicationCost={applicationCostRecord}
        applicationGstInfo={applicationGstInfo}
        departmentGstInfo={departmentGstInfo}
        clientData={clientData}
        invoiceSetting={bankDetails}
        saving={updatingInvoice}
        updatingDiscount={updatingInvoiceDiscount}
        loadingInvoice={loadingEditInvoice}
        paymentForm={{
          payment,
          onPaymentChange: handlePaymentChange,
          receiptFile,
          receiptPreview,
          onReceiptChange: handleReceiptChange,
          onReceiptRemove: removeReceipt,
          onDrop: handleDrop,
          onDragOver: handleDragOver,
          creditsAvailable,
          remainingBalance: ledgerTotals?.remaining_balance,
          loading: loadingPayment,
          onSubmit: () => addPayment({ invoiceId: editingInvoice?.id }),
          onCancel: resetPaymentForm,
        }}
        ledgerEntries={applicationLedgerDetails}
        applicationInvoices={applicationInvoices}
        onReversePayment={reversePayment}
        reversingPayment={loadingPayment}
        onAddInvoiceNote={addInvoiceNote}
        addingInvoiceNote={addingInvoiceNote}
        invoiceNotes={editingInvoiceNotes}
        loadingInvoiceNotes={loadingInvoiceNotes}
        discountFeeRules={discountFeeRules}
      />
      {showInvoicePreviewModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 1075,
            }}
            onClick={() => setShowInvoicePreviewModal(false)}
          />
          <div
            className="modal fade show d-block invoice-preview-modal"
            tabIndex={-1}
            style={{ zIndex: 1080 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
              <div className="modal-content border-0 rounded-4 shadow">
                <div className="modal-header">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <h5 className="modal-title fw-bold mb-0">Invoice preview</h5>
                    <span
                      className={getInvoiceStatusBadgeClass(
                        selectedHistoryInvoice?.status ||
                          deriveInvoiceStatusFromPayment({
                            dueAmount: ledgerTotals?.remaining_balance || 0,
                            paidAmount: ledgerTotals?.total_payments || 0,
                            dueDate:
                              selectedHistoryInvoice?.due_date ||
                              selectedHistoryInvoice?.dueDate ||
                              invoiceDueDate,
                          }),
                      )}
                    >
                      {getInvoiceStatusLabel(
                        selectedHistoryInvoice?.status ||
                          deriveInvoiceStatusFromPayment({
                            dueAmount: ledgerTotals?.remaining_balance || 0,
                            paidAmount: ledgerTotals?.total_payments || 0,
                            dueDate:
                              selectedHistoryInvoice?.due_date ||
                              selectedHistoryInvoice?.dueDate ||
                              invoiceDueDate,
                          }),
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowInvoicePreviewModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="invoice-preview-container">
                    {historyXeroPreviewDraft ? (
                      <InvoiceXeroPreview
                        draft={historyXeroPreviewDraft}
                        onClose={() => setShowInvoicePreviewModal(false)}
                      />
                    ) : (
                    <InvoicePreview
                      clientName={getClientNameFromRecord(clientData)}
                      clientEmail={getClientEmailFromRecord(clientData)}
                      invoiceNo={
                        selectedHistoryInvoice?.invoice_no ||
                        selectedHistoryInvoice?.invoiceNo ||
                        invoiceNo
                      }
                      invoiceDate={
                        selectedHistoryInvoice?.cost_date ||
                        selectedHistoryInvoice?.created_at ||
                        invoiceCostDate
                      }
                      dueDate={
                        selectedHistoryInvoice?.due_date ||
                        selectedHistoryInvoice?.dueDate ||
                        invoiceDueDate
                      }
                      reference={
                        selectedHistoryInvoice?.reference || invoiceReference
                      }
                      description={
                        selectedHistoryInvoice?.description ||
                        invoiceDefaultDescription
                      }
                      currency={
                        selectedHistoryInvoice?.currency ||
                        selectedApplication?.currency ||
                        "AUD"
                      }
                      totalCharges={ledgerTotals?.total_charges || 0}
                      payments={paymentsNotReversed}
                      discounts={discountsNotReversed}
                      paidTotal={ledgerTotals?.total_payments || 0}
                      discountTotal={ledgerTotals?.total_discounts || 0}
                      remainingBalance={ledgerTotals?.remaining_balance || 0}
                      gstInfo={applicationGstInfo}
                      company={{
                        name: "Aptitude Migration Pty Ltd",
                        address: bankDetails?.c_address || "",
                        phone: bankDetails?.c_phone || "",
                        mobile: bankDetails?.c_mobile || "",
                        email: bankDetails?.c_email || "",
                        abn: bankDetails?.abn || "",
                        b_account_name: bankDetails?.b_account_name || "",
                        bsb: bankDetails?.bsb || "",
                        acc: bankDetails?.acc || "",
                        bankName: bankDetails?.b_name || "",
                      }}
                      appId={selectedApplication?.id}
                      applicationType={type}
                      invoiceStatus={
                        selectedHistoryInvoice?.status ||
                        deriveInvoiceStatusFromPayment({
                          dueAmount: ledgerTotals?.remaining_balance || 0,
                          paidAmount: ledgerTotals?.total_payments || 0,
                          dueDate:
                            selectedHistoryInvoice?.due_date ||
                            selectedHistoryInvoice?.dueDate ||
                            invoiceDueDate,
                        })
                      }
                    />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DiscountModal;
